import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppDayFloat - the day label in the contact page floats at the top of the conversation ONLY WHILE THE OWNER SCROLLS.
//
// ⛔ WHY (owner P0, 2026-09-28, the new chat "Dinar": "There is a missing message in this chat"). MEASURED on the live
// page and in real Chrome at the owner's pane height (870 x 560): the page held all 6 messages under "6 messages", but
// the chat opens at its newest message (`column-reverse`) and the day's FIRST message was 1,335 px tall - wholly above
// the view. The sticky label (`DossierHtml.ChatCss`, `position:sticky;top:4px`, added 2026-09-28) was pinned at the top
// of the pane, over the words of the bubble under it ("covers 'Energi'"), and "28 SEPTEMBER 6" pinned there read as the
// START of the day - so the owner counted 5 bubbles and reported one missing. Nothing was missing; the label lied about
// where the day began and hid text doing it.
// ⭐ THE RULE, AS WHATSAPP DOES IT: while a scroll THE OWNER started is under way (and `IdleMs` after), the frame's
// <html> carries `data-wac-scrolling` and the label may pin; at rest every label is back in its own row above its
// day - never over a message - and a bubble cut off at the top of the pane is what says "more above". A pointer on the
// pill, or the keyboard on its checkbox, keeps it pinned (`Css`).
// ⚠ "The owner started it": a scroll within `IntentMs` of a wheel / touch / pointer / key in the frame, or one that
// continues a floating scroll (a phone's momentum). A scroll made by code alone (the live feed following the newest
// message, `AppDayFold` keeping a label under the pointer) floats nothing.
// ⚠ The page on its own (a file opened from Dropbox, no app) keeps the pinned label - these rules are the APP's frame
// sheet (`AppMain.Frame`), keyed on `html[data-wac-embedded]`, so old pages get the fix without a rebuild.
// ⛔ NOTHING HERE SENDS OR WRITES ANYTHING - it sets one attribute on the frame's <html>.
// ⚠ SHAPED BY INTERFACES, so the suite drives it with a small fake frame.

export interface IDflRoot {
    setAttribute(Name: string, Value: string): void;
    removeAttribute(Name: string): void;
}

export interface IDflDoc {
    documentElement: IDflRoot | null;
    addEventListener(Type: string, Listener: (Ev: Event) => void, Capture: boolean): void;
}

export interface IDflWin {
    setTimeout(Callback: () => void, Ms: number): number;
    clearTimeout(Id: number): void;
}

export interface IDflFrame {
    contentDocument: IDflDoc | null;
    contentWindow: IDflWin | null;
    addEventListener(Type: string, Listener: () => void): void;
}

export class AppDayFloat {
    /** The frame's <html> attribute the CSS keys on while the owner scrolls. */
    public static readonly Attr: string = "data-wac-scrolling";
    /** How long the label stays pinned after the last scroll. */
    public static readonly IdleMs: number = 1200;
    /** A scroll this soon after the owner's own input is theirs. */
    public static readonly IntentMs: number = 1000;
    /** What counts as the owner's own input inside the frame. */
    public static readonly IntentEvents: string[] = ["wheel", "touchstart", "touchmove", "pointerdown", "keydown"];
    /** The clock - a field so the suite can move it. */
    public static Now = (): number => {
        return Date.now();
    };

    private Frame: IDflFrame | null = null;
    private Doc: IDflDoc | null = null;
    private Win: IDflWin | null = null;
    private Timer: number = 0;
    private Floating: boolean = false;
    private LastInputMs: number = Number.NEGATIVE_INFINITY;

    public Attach = (Frame: IDflFrame): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Frame = Frame;
            Frame.addEventListener("load", this.OnLoad);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFloat.Attach");
        }
    };

    /** The page is in: listen for the owner's input and for scrolls (capture - a scroll does not bubble) - once. */
    public OnLoad = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Frame === null || this.Frame.contentDocument === null || this.Doc !== null) { return; }
            this.Doc = this.Frame.contentDocument as IDflDoc;
            this.Win = this.Frame.contentWindow;
            for (const Type of AppDayFloat.IntentEvents) { (this.Doc as IDflDoc).addEventListener(Type, this.OnIntent, true); }
            (this.Doc as IDflDoc).addEventListener("scroll", this.OnScroll, true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFloat.OnLoad");
        }
    };

    public OnIntent = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.LastInputMs = AppDayFloat.Now();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFloat.OnIntent");
        }
    };

    /** A scroll the owner started (or one continuing a floating scroll): pin the label until it has been still a while. */
    public OnScroll = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Doc === null || this.Win === null) { return; }
            if (this.Floating === false && AppDayFloat.Now() - this.LastInputMs > AppDayFloat.IntentMs) { return; }
            const Root: IDflRoot | null = (this.Doc as IDflDoc).documentElement;
            if (Root === null) { return; }
            (Root as IDflRoot).setAttribute(AppDayFloat.Attr, "1");
            this.Floating = true;
            if (this.Timer !== 0) { (this.Win as IDflWin).clearTimeout(this.Timer); }
            this.Timer = (this.Win as IDflWin).setTimeout(this.OnIdle, AppDayFloat.IdleMs);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFloat.OnScroll");
        }
    };

    /** Still for `IdleMs`: every label back in its own row. */
    public OnIdle = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Timer = 0;
            this.Floating = false;
            if (this.Doc === null || (this.Doc as IDflDoc).documentElement === null) { return; }
            ((this.Doc as IDflDoc).documentElement as IDflRoot).removeAttribute(AppDayFloat.Attr);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFloat.OnIdle");
        }
    };

    /**
     * The frame-sheet rules: in the app a label is in its row at rest, pinned only while scrolling, hovered or focused.
     * They beat the page's own `.dayhead{position:sticky;top:4px}` by specificity, so an OLD page is fixed too.
     * DOCUMENTED EXCEPTION: a constant stylesheet. ERROR-SUPPRESSED-JUSTIFIED: returns a literal.
     */
    public static Css = (): string => {
        const Mark: string = "html[data-wac-embedded]";
        return Mark + " .dayhead{position:relative;top:auto}"
            + Mark + "[" + AppDayFloat.Attr + "] .dayhead," + Mark + " .dayhead:has(span:hover),"
            + Mark + " .day:has(.daybox:focus-visible) .dayhead{position:sticky;top:4px}"
            // ⭐ The business look (2026-10-13): at rest the label is plain words between two hairlines (`AppTokens.FrameFlat`).
            // While it floats over the messages the hairlines would cross the bubbles, so they go, and the words get an edge.
            + Mark + "[" + AppDayFloat.Attr + "] .dayhead::before," + Mark + "[" + AppDayFloat.Attr + "] .dayhead::after{visibility:hidden}"
            + Mark + "[" + AppDayFloat.Attr + "] .dayhead span{box-shadow:inset 0 0 0 1px var(--line)}";
    };
}
